import {
  takeEvery,
  put,
  select,
  cancel,
  call,
  fork,
  all
} from "redux-saga/effects";
import {
  filterAudienceTargetingClauses,
  getFilteredClauseTree,
  getNestedConditionsFromFlattenedClauses,
  clauseKeys
} from "@appcues/libcues";
import * as actionTypes from "constants/actionTypes";
import { trackEvent } from "actions/events";
import { setAudienceSegmentAndFlushRule } from "actions/account/conditions";
import { selectUserId } from "reducers/user";
import { selectAccountStepConditions } from "reducers/account/conditions";
import { selectAccountSegment } from "reducers/account/segments";
import { navigate } from "actions/routing";
import { firebaseSet, remove, push } from "sagas/firebase/lib";
import { showDelayedToast } from "sagas/toaster";
import { selectAccountId } from "reducers/account/meta";
import { showToast } from "actions/toaster";
import {
  deleteSegmentInStore,
  replaceAccountSegment,
  updateSegmentInStore
} from "actions/segments";
import { reportError } from "helpers/error-reporting";
import { hitPublishHook } from "sagas/account/publishing";

/**
 * Helper saga, calls createSegment for `segments`
 *
 * @param action
 */

export function* createAudienceSegment(action) {
  const { name, description, conditions } = action.payload;

  const segmentId = yield call(createSegment, name, description, conditions);
  yield put(navigate(`/segments/${segmentId}/view`));
}

/**
 * Helper saga, gathers `conditions` from a rule
 * and creates a segment from them
 *
 * @param action
 */

export function* createSegmentFromRule(action) {
  const { name, stepId } = action.payload;

  // we'll base the new segment on the audience branch of
  // the provided step's rule conditions

  const stepClauses = yield select(selectAccountStepConditions, stepId);
  const audienceClauses = yield call(
    getFilteredClauseTree,
    filterAudienceTargetingClauses,
    stepClauses
  );

  const conditions = yield call(
    getNestedConditionsFromFlattenedClauses,
    audienceClauses,
    null,
    clauseKeys
  );

  const segmentId = yield call(
    createSegment,
    name,
    `created from targeting on flow ${stepId}`,
    conditions
  );

  yield put(setAudienceSegmentAndFlushRule(stepId, segmentId));
}

/**
 * Creates a new segment or bookmark from the specified branch of the current
 * conditions tree and saves in Firebase with a newly generated ID.
 *
 * If segment already exists (i.e. segmentId argument provided),
 * simply update it and flush.
 *
 * @param name
 * @param conditions
 */

export function* createSegment(
  name = "New Segment",
  description = "",
  conditions
) {
  const showDelayedToastJob = yield fork(
    showDelayedToast,
    { type: "standard", message: "Still creating Segment..." },
    1500
  );

  const now = yield call([Date, Date.now]);
  const accountId = yield select(selectAccountId);
  const userId = yield select(selectUserId);

  try {
    const newSegment = {
      name,
      description,
      conditions,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
      updatedBy: userId
    };

    const segmentId = yield call(push, {
      [`accounts/${accountId}/segments`]: newSegment
    });

    try {
      yield call(hitPublishHook);
    } catch (e) {
      yield put(
        showToast({ type: "error", message: "Failed to publish segment" })
      );
      yield call(reportError, e);
    }

    try {
      yield put(
        trackEvent("Created a Segment", {
          name,
          segment_id: segmentId
        })
      );
    } catch (e) {
      yield call(reportError, e);
    }

    yield all([
      cancel(showDelayedToastJob),
      put(
        showToast({ type: "success", message: "Segment created successfully" })
      )
    ]);

    return segmentId;
  } catch (e) {
    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: "error", message: "Failed to create segment" }))
    ]);

    yield call(reportError, e);
  }

  return undefined;
}

/**
 * Applies the specified changes to the segment in `account/segments`
 * reducer, then persists the new value to Firebase
 *
 * @param action
 */

export function* updateSegmentAndFlushSegment(action) {
  const { segmentId, changes } = action.payload;

  const showDelayedToastJob = yield fork(
    showDelayedToast,
    { type: "standard", message: "Still saving Segment..." },
    5000
  );

  const now = yield call([Date, Date.now]);
  const accountId = yield select(selectAccountId);
  const userId = yield select(selectUserId);

  // grab a copy of the segment as it currently exists to roll back to
  const rollbackSegment = yield select(selectAccountSegment, segmentId);

  try {
    yield put(
      updateSegmentInStore(segmentId, {
        ...changes,
        updatedAt: now,
        updatedBy: userId
      })
    );

    const updatedSegment = yield select(selectAccountSegment, segmentId);

    yield call(
      firebaseSet,
      `accounts/${accountId}/segments/${segmentId}`,
      updatedSegment
    );

    try {
      yield call(hitPublishHook);
    } catch (e) {
      yield put(
        showToast({ type: "error", message: "Failed to publish segment" })
      );
      yield call(reportError, e);
    }

    try {
      yield put(
        trackEvent("Updated a Segment", {
          segment_id: segmentId
        })
      );
    } catch (e) {
      yield call(reportError, e);
    }

    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: "success", message: "Segment saved successfully" }))
    ]);

    yield put(navigate(`/segments/${segmentId}/view`));
  } catch (e) {
    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: "error", message: "Failed to update segment" }))
    ]);

    yield call(reportError, e);
    yield put(replaceAccountSegment(segmentId, rollbackSegment));
  }
}

export function* cloneSegment(action) {
  const { segmentId } = action.payload;

  const showDelayedToastJob = yield fork(
    showDelayedToast,
    { type: "standard", message: "Still cloning Segment..." },
    1500
  );

  const now = yield call([Date, Date.now]);
  const accountId = yield select(selectAccountId);
  const userId = yield select(selectUserId);

  const originalSegment = yield select(selectAccountSegment, segmentId);

  try {
    const newSegment = {
      ...originalSegment,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
      name: `Clone of ${originalSegment.name}`,
      clonedFrom: originalSegment.id
    };

    const newSegmentId = yield call(push, {
      [`accounts/${accountId}/segments`]: newSegment
    });

    try {
      yield call(hitPublishHook);
    } catch (e) {
      yield put(
        showToast({ type: "error", message: "Failed to publish segment" })
      );
      yield call(reportError, e);
    }

    try {
      yield put(
        trackEvent("Cloned a Segment", {
          name: newSegment.name,
          segment_id: newSegmentId,
          cloned_from_id: originalSegment.id
        })
      );
    } catch (e) {
      yield call(reportError, e);
    }

    yield all([
      cancel(showDelayedToastJob),
      put(
        showToast({ type: "success", message: "Segment cloned successfully" })
      )
    ]);
  } catch (e) {
    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: "error", message: "Failed to clone segment" }))
    ]);

    yield call(reportError, e);
  }
}

export function* deleteSegmentAndFlushSegment(action) {
  const { segmentId } = action.payload;

  const showDelayedToastJob = yield fork(
    showDelayedToast,
    { type: "standard", message: "Still deleting Segment..." },
    1500
  );

  const accountId = yield select(selectAccountId);
  const rollbackSegment = yield select(selectAccountSegment, segmentId);

  try {
    yield put(deleteSegmentInStore(segmentId));
    yield call(remove, [`accounts/${accountId}/segments/${segmentId}`]);

    try {
      yield call(hitPublishHook);
    } catch (e) {
      yield put(
        showToast({ type: "error", message: "Failed to publish segment" })
      );
      yield call(reportError, e);
    }

    try {
      yield put(
        trackEvent("Deleted a Segment", {
          segment_id: segmentId
        })
      );
    } catch (e) {
      yield call(reportError, e);
    }

    yield all([
      cancel(showDelayedToastJob),
      put(
        showToast({ type: "success", message: "Segment deleted successfully" })
      )
    ]);
  } catch (e) {
    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: "error", message: "Failed to delete segment" }))
    ]);

    yield call(reportError, e);
    yield put(replaceAccountSegment(segmentId, rollbackSegment));
  }
}

export function* segments() {
  yield takeEvery(actionTypes.CREATE_AUDIENCE_SEGMENT, createAudienceSegment);
  yield takeEvery(actionTypes.CREATE_SEGMENT_FROM_RULE, createSegmentFromRule);
  yield takeEvery(
    actionTypes.UPDATE_SEGMENT_AND_FLUSH_SEGMENT,
    updateSegmentAndFlushSegment
  );
  yield takeEvery(
    actionTypes.DELETE_SEGMENT_AND_FLUSH_SEGMENT,
    deleteSegmentAndFlushSegment
  );
  yield takeEvery(actionTypes.CLONE_SEGMENT, cloneSegment);
}

export default segments;
